import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, ScanSearch, Eye, Map, Users, Star, Zap, FileText, Globe, LucideIcon } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, LucideIcon> = {
  ScanSearch, Eye, Map, Users, Star, Zap, FileText, Globe,
};

const DEFAULT_FEATURES = [
  { icon: "Eye", title: "Live Match Assessment", desc: "Our scouts attend local leagues, tournaments, and school competitions to identify talent in their natural environment." },
  { icon: "Map", title: "Multi-Country Network", desc: "Active scouting presence across Nigeria, Ghana, Senegal, Germany, UK, and North America — covering all major talent hubs." },
  { icon: "Star", title: "Player Profiling", desc: "Each prospect receives a comprehensive profile covering technical attributes, physical data, mental aptitude, and career history." },
  { icon: "Users", title: "Referral Programme", desc: "Coaches, teachers, and community leaders can refer talented athletes directly to our scouting team for evaluation." },
  { icon: "Zap", title: "Fast-Track Pathway", desc: "Outstanding talent can be fast-tracked into our academy program or connected directly with professional club trials." },
  { icon: "ScanSearch", title: "Video Analysis", desc: "Submit your highlight reel for remote scouting evaluation — we review submissions from anywhere in the world." },
];

const DEFAULT_BULLETS = [
  "Comprehensive database of scouted talent across Africa and Europe",
  "Regular scouting tournaments attended by international club representatives",
  "Objective rating system assessing technical, tactical, physical, and psychological traits",
  "Direct lines of communication with Tier 1 and Tier 2 European clubs",
  "Ethical scouting practices prioritizing the welfare and education of young athletes",
];

export default function TalentScouting() {
  const { getMeta, getText } = useCms();
  const meta = getMeta<{
    heading?: string;
    highlight?: string;
    subtext?: string;
    heroImage?: string;
    overviewHeading?: string;
    overviewText?: string;
    overviewImage?: string;
    bullets?: string[];
    features?: { icon: string; title: string; desc: string }[];
  }>("service_scouting", {});

  const heading = meta.heading ?? "TALENT";
  const highlight = meta.highlight ?? "SCOUTING";
  const subtext = meta.subtext ?? getText("service_scouting", "Our global scouting network identifies exceptional athletes across Africa, Europe, and North America — giving every talent a fair chance to shine.");
  const heroImage = meta.heroImage || "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=1600&auto=format&fit=crop";
  const overviewHeading = meta.overviewHeading ?? "HOW OUR SCOUTING WORKS";
  const overviewText = meta.overviewText ?? "Eliada's scouting division operates one of the most active talent identification networks in sub-Saharan Africa and the African diaspora. Our team of experienced scouts and analysts use a combination of live observation and digital tools to find the best players.\n\nWe evaluate players at all levels — from grassroots youth football to semi-professional leagues — ensuring that no talent is overlooked based on geography or financial status.";
  const overviewImage = meta.overviewImage || "https://images.unsplash.com/photo-1556056504-5c7696c4c28d?w=900&auto=format&fit=crop";
  const bullets = (Array.isArray(meta.bullets) && meta.bullets.length > 0) ? meta.bullets : DEFAULT_BULLETS;
  const features = (Array.isArray(meta.features) && meta.features.length > 0) ? meta.features : DEFAULT_FEATURES;

  return (
    <>
      <SeoHead
        title={`${heading} ${highlight}`}
        description={subtext}
        path="/services/talent-scouting"
      />
      <PageTransition className="pt-24 pb-0">
        {/* Hero */}
        <div className="relative min-h-[55vh] flex items-center overflow-hidden">
          <img src={heroImage} alt={`${heading} ${highlight}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
              <ScanSearch size={14} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 max-w-2xl leading-tight">
              {heading} <span className="text-primary">{highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-xl mb-8 leading-relaxed">
              {subtext}
            </p>
            <Link href="/join" onClick={() => {}}>
              <Button size="lg" className="gap-2">Get Scouted <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Overview */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  {overviewHeading}
                </h2>
                <div className="space-y-4 mb-8">
                  {overviewText.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-600 text-lg leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
                <ul className="space-y-3">
                  {bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="text-primary shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img src={overviewImage} alt="Scouting" className="rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                SCOUTING <span className="text-primary">SERVICES</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive scouting solutions for athletes, clubs, and partner academies.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const IconComponent = ICON_MAP[f.icon] || ScanSearch;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-background p-8 rounded-2xl border border-white/5 hover:border-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                      <IconComponent className="text-primary w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">THINK YOU HAVE WHAT IT TAKES?</h2>
            <p className="text-xl text-gray-800 mb-10 font-medium">Submit your athletic profile and video footage to our scouting team for evaluation.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/join" onClick={() => {}}><Button size="lg" className="bg-gray-900 text-white hover:bg-black">Submit Profile</Button></Link>
              <Link href="/contact" onClick={() => {}}><Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white bg-transparent">Talk to a Scout</Button></Link>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
}
